import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Target,
  FileText,
  Brain,
  CheckCircle2,
  Clock,
  Lock,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const APEX_SIX = [
  { key: 'composure',  label: 'Composure',  framework: 'The 3-Breath Reset' },
  { key: 'confidence', label: 'Confidence', framework: 'The ABC Confidence Stack' },
  { key: 'awareness',  label: 'Awareness',  framework: 'Green-Yellow-Red Awareness' },
  { key: 'focus',      label: 'Focus',      framework: 'The Camera Drill' },
  { key: 'resilience', label: 'Resilience', framework: 'Bounce-Back 3R' },
  { key: 'preparation',label: 'Preparation',framework: 'The Performance Pyramid' },
];

const FAQ = [
  {
    q: 'How is this different from the free Mindset Quiz?',
    a: 'The free quiz gives you an archetype + one framework. The $27 Assessment scores you across all six Apex dimensions, builds a personalized Individual Development Plan (IDP), and prescribes a 30-day action sequence — plus a PDF you can keep.',
  },
  {
    q: 'How long does it take?',
    a: '6–8 minutes. 18 questions on a simple 1–5 scale, no writing required.',
  },
  {
    q: 'Who is this for?',
    a: (
      <>
        <b>Athletes only</b> — serious middle school, high school, and college competitors who want a clear-eyed read on the mental side of their game and a real plan, not just a quiz result.
        <br /><br />
        If you are a parent or a coach, this assessment is built for the athlete to take themselves. We have free tools built specifically for you:
        <ul className="mt-3 space-y-1.5 list-disc pl-5">
          <li>
            Parents → <a href="/quiz" className="text-[#004C54] font-semibold underline underline-offset-2 hover:text-[#003a40]" data-testid="sales-faq-parent-link">Parent Mindset Quiz</a>
          </li>
          <li>
            Coaches → <a href="/coaches-reset" className="text-[#004C54] font-semibold underline underline-offset-2 hover:text-[#003a40]" data-testid="sales-faq-coach-link">Coach's Reset</a>
          </li>
        </ul>
      </>
    ),
  },
  {
    q: 'Do I need an account?',
    a: 'Nope. You get a secure link after checkout that unlocks the assessment and your results for 30 days.',
  },
  {
    q: 'What if I want to talk it through with Coach Lewis?',
    a: 'Every IDP includes a unique code for 25% off your first 1:1 session — valid for 7 days from when you complete the assessment. Just paste the code into the Calendly notes when you book.',
  },
];

export default function AssessmentSales() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wasCanceled = searchParams.get('canceled') === '1';
  const [meta, setMeta] = useState({ price_label: '$27', question_count: 18 });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    fetch(`${BACKEND}/api/assessment/meta`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setMeta(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'Apex Six Athlete Self-Assessment — Get Your IDP';
  }, []);

  const startCheckout = async () => {
    // Implicit consent: clicking "Continue to athlete checkout" constitutes
    // affirmative assent to the Terms + Privacy Policy linked beneath the
    // button (browsewrap-with-conspicuous-notice pattern used by Stripe,
    // ConvertKit, Substack, OpenAI, etc.). Stripe checkout itself also
    // surfaces the terms as a second touchpoint.
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/assessment/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim() || undefined,
          name: name.trim() || undefined,
          origin_url: window.location.origin,
        }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        toast.error(b.detail || 'Could not start checkout. Try again in a moment.');
        setLoading(false);
        return;
      }
      const data = await r.json();
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        toast.error('Checkout URL missing. Please try again.');
        setLoading(false);
      }
    } catch {
      toast.error('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0a0d0e]" data-testid="assessment-sales">
      {/* Minimal header — no main nav (hidden product) */}
      <header className="border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="sales-home-link">
            <div className="w-7 h-7 rounded-md bg-[#004C54] text-white flex items-center justify-center font-bold">A</div>
            <span className="font-semibold tracking-tight">Apex Athletic Mind</span>
          </a>
          <Badge variant="outline" className="hidden sm:inline-flex border-[#004C54]/30 text-[#004C54] uppercase tracking-[0.2em] text-[10px] font-bold">
            <Lock className="h-3 w-3 mr-1" /> Athletes Only · Premium IDP
          </Badge>
        </div>
      </header>

      {wasCanceled && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sales-canceled-banner">
            Checkout was canceled — no charge. You can try again whenever you're ready.
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-12">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#004C54]/15 bg-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#004C54] font-bold mb-5" data-testid="sales-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> Athletes Only · Premium IDP · {meta.price_label}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[58px] font-bold tracking-tight leading-[1.05]" data-testid="sales-headline">
              Stop guessing what&rsquo;s holding your game back.
              <br className="hidden sm:block" />
              <span className="text-[#004C54]"> Find out in 8 minutes.</span>
            </h1>
            <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-[58ch]" data-testid="sales-subhead">
              The <b>Apex Six Athlete Self-Assessment</b> scores you across the six
              mental-performance dimensions Coach Lewis runs <b>in-person</b> with serious middle
              school, high school, and college athletes &mdash; then turns the result into your
              personalized <b>Individual Development Plan</b>: the one framework to install first,
              three drills to run this week, and a unique code for <b>25% off your first 1:1
              session</b> if booked within 7 days.
            </p>
            <p className="mt-3 text-[14px] text-foreground/60 leading-relaxed max-w-[58ch] italic" data-testid="sales-subhead-anchor">
              In-person framework &mdash; not an app or generic online course.
            </p>

            {/* Trust bar — 3 credibility bullets immediately above the CTAs */}
            <ul className="mt-6 space-y-2.5 max-w-[60ch]" data-testid="sales-trust-bar">
              <li className="flex items-start gap-2.5 text-[14px] leading-[1.55] text-[#0a0d0e]">
                <CheckCircle2 className="h-[18px] w-[18px] text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                <span><b>{meta.question_count} questions · 6 dimensions</b> · scored against the framework behind a Non-Public B State Championship program</span>
              </li>
              <li className="flex items-start gap-2.5 text-[14px] leading-[1.55] text-[#0a0d0e]">
                <CheckCircle2 className="h-[18px] w-[18px] text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                <span><b>Personalized PDF IDP delivered to your inbox</b> &mdash; yours to keep, share with your coach</span>
              </li>
              <li className="flex items-start gap-2.5 text-[14px] leading-[1.55] text-[#0a0d0e]">
                <CheckCircle2 className="h-[18px] w-[18px] text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                <span>Built by <b>Coach Varsay Lewis, M.Ed.</b> · ELVTR Sports Psychology Course Completed · Asst. Head Coach, Montclair Kimberley Academy (NJ)</span>
              </li>
            </ul>

            <div className="mt-4 rounded-lg border border-[#004C54]/15 bg-white px-4 py-3 text-[13px] text-foreground/70 max-w-[58ch]" data-testid="sales-audience-clarifier">
              <span className="font-semibold text-[#0a0d0e]">Parent or coach?</span> This assessment
              is for the athlete to take themselves. If you're a{' '}
              <a href="/quiz" className="text-[#004C54] font-semibold underline underline-offset-2 hover:text-[#003a40]" data-testid="sales-audience-parent-link">parent</a>{' '}
              or{' '}
              <a href="/coaches-reset" className="text-[#004C54] font-semibold underline underline-offset-2 hover:text-[#003a40]" data-testid="sales-audience-coach-link">coach</a>,
              we've built separate (free) tools for you.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={startCheckout}
                disabled={loading}
                size="lg"
                className="h-12 px-7 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white font-semibold tracking-tight"
                data-testid="sales-primary-cta"
              >
                {loading ? 'Starting\u2026' : `Get my Athlete IDP \u2014 ${meta.price_label}`} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                href="/quiz"
                className="inline-flex h-12 items-center justify-center rounded-full border border-foreground/15 px-6 text-sm font-semibold text-foreground/80 hover:bg-foreground/[0.04] transition-colors"
                data-testid="sales-secondary-cta"
              >
                Not ready? Take the free 2-min Mindset Quiz first
              </a>
            </div>

            <p className="mt-3 text-[12px] text-foreground/55 italic max-w-[58ch]" data-testid="sales-cta-microcopy">
              Athletes only. Parents and coaches &mdash; we have free tools built for you.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-[12px] text-foreground/55">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 6–8 min</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Stripe secure checkout</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Token access · 30 days</span>
            </div>
          </div>

          {/* IDP preview card */}
          <div className="relative">
            <Card className="shadow-[0_24px_64px_rgba(0,76,84,0.15)] border-[#E5E7E7] overflow-hidden" data-testid="sales-idp-preview">
              <div className="bg-[#004C54] px-6 py-4">
                <div className="text-[10px] tracking-[0.2em] font-bold text-white/70 font-mono uppercase">Individual Development Plan</div>
                <div className="text-white font-semibold text-lg mt-0.5">Apex Six · Sample</div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-baseline gap-3">
                  <div className="text-5xl font-bold text-[#004C54] leading-none">73</div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-foreground/55 font-mono font-semibold">Overall</div>
                    <div className="text-sm font-semibold">Solid but Uneven</div>
                  </div>
                </div>
                <div className="mt-5 space-y-2.5">
                  {[
                    ['Preparation', 88],
                    ['Focus', 80],
                    ['Resilience', 73],
                    ['Confidence', 67],
                    ['Composure', 58],
                    ['Awareness', 53],
                  ].map(([label, val]) => (
                    <div key={label} className="flex items-center gap-3">
                      <div className="w-24 text-xs font-semibold text-foreground/70">{label}</div>
                      <div className="flex-1 h-2 rounded-full bg-[#F0F4F4] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${val >= 70 ? 'bg-[#4FBCC7]' : 'bg-[#004C54]'}`}
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-xs font-semibold tabular-nums">{val}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-lg bg-[#F0F4F4] p-4">
                  <div className="text-[10px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">Primary Action</div>
                  <div className="font-semibold mt-1">Awareness → Green-Yellow-Red Awareness</div>
                  <div className="text-xs text-foreground/60 mt-1">+ 3 specific drills to install over the next 30 days.</div>
                </div>
              </CardContent>
            </Card>
            <div className="absolute -bottom-4 -right-4 hidden sm:block w-32 h-32 rounded-full bg-[#4FBCC7]/15 blur-2xl" />
          </div>
        </div>
      </section>

      {/* What you get */}
      <section id="what-you-get" className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="max-w-2xl">
          <div className="text-[11px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">What's inside</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Everything you need to install the next 30 days.</h2>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            { icon: Brain, title: '6-Dimension Mental Skills Score', body: 'Composure, Confidence, Awareness, Focus, Resilience, Preparation — scored 0–100 each, with a band and a coach-voice narrative for your top strengths and priorities.' },
            { icon: Target, title: 'Apex Six Framework Prescription', body: 'Your #1 priority dimension auto-prescribes the exact Coach Lewis framework to install — and 3 concrete drills to run this week.' },
            { icon: FileText, title: 'Printable IDP PDF', body: 'A branded multi-page IDP PDF emailed to you (and downloadable from your secure link) so you can take it to practice, share with parents, or hand to your coach.' },
          ].map((b) => (
            <Card key={b.title} className="border-[#E5E7E7]" data-testid={`sales-benefit-${b.title.split(' ')[0].toLowerCase()}`}>
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#004C54]/10 text-[#004C54] flex items-center justify-center">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{b.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Six dimensions visual list */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="max-w-2xl">
          <div className="text-[11px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">The Apex Six</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Six dimensions. Six prescriptions.</h2>
          <p className="mt-4 text-foreground/65">Every dimension maps 1:1 to a Coach Lewis framework — so the moment we see where your gap is, we know exactly what to install.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {APEX_SIX.map((d, i) => (
            <div key={d.key} className="rounded-xl border border-[#E5E7E7] bg-white p-5" data-testid={`sales-dim-${d.key}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">Dim {i + 1}</div>
                <Zap className="h-4 w-4 text-[#4FBCC7]" />
              </div>
              <div className="mt-2 font-heading text-lg font-semibold tracking-tight">{d.label}</div>
              <div className="text-xs text-foreground/55 mt-1">→ {d.framework}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing / CTA card */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <Card className="border-[#004C54]/20 shadow-[0_24px_64px_rgba(0,76,84,0.12)] overflow-hidden" data-testid="sales-pricing-card">
          <div className="bg-[#004C54] px-6 py-5 text-white text-center">
            <div className="text-[11px] tracking-[0.25em] font-bold font-mono uppercase opacity-80">For Athletes Only · Premium IDP</div>
            <div className="mt-2 font-heading text-5xl font-bold tracking-tight">{meta.price_label}<span className="text-base font-normal opacity-70"> one-time</span></div>
            <div className="text-sm opacity-80 mt-1">{meta.question_count} questions · 6 dimensions · 30-day plan</div>
          </div>
          <CardContent className="p-6 sm:p-8">
            <ul className="space-y-2.5 mb-6">
              {[
                'Personalized 6-dimension Mindset score',
                'Coach-voice narrative: strengths + priorities',
                'Apex Six framework prescription + 3 drills',
                'Branded multi-page IDP PDF (emailed + downloadable)',
                '25% off your first 1:1 session (book within 7 days)',
                '30-day secure access via your private link',
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-[#4FBCC7] shrink-0 mt-px" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-2.5">
              <Label htmlFor="sales-name" className="text-xs font-semibold text-foreground/70">First name (optional)</Label>
              <Input
                id="sales-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex"
                className="bg-white"
                data-testid="sales-name-input"
              />
              <Label htmlFor="sales-email" className="text-xs font-semibold text-foreground/70 mt-2">Email (optional — Stripe will collect it too)</Label>
              <Input
                id="sales-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@example.com"
                className="bg-white"
                data-testid="sales-email-input"
              />
            </div>
            <Button
              onClick={startCheckout}
              disabled={loading}
              size="lg"
              className="w-full mt-4 h-12 rounded-full bg-[#0a0d0e] hover:bg-black text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="sales-pricing-cta"
            >
              {loading ? 'Starting…' : `Continue to athlete checkout · ${meta.price_label}`} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            {/* Implicit consent disclosure — replaces the previous checkbox.
                Clicking "Continue" counts as affirmative assent to the linked
                terms. Standard pattern for paid conversion flows. */}
            <p
              className="mt-2.5 text-[11.5px] text-foreground/55 text-center leading-snug"
              data-testid="sales-implicit-consent"
            >
              By continuing, you agree to our{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#004C54] font-semibold hover:underline"
                data-testid="sales-terms-link"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#004C54] font-semibold hover:underline"
                data-testid="sales-privacy-link"
              >
                Privacy Policy
              </a>
              .
            </p>
            <div className="text-[11px] text-foreground/50 text-center mt-2 inline-flex items-center justify-center gap-1.5 w-full">
              <ShieldCheck className="h-3.5 w-3.5" /> Payment processed by Stripe · No subscription · 30-day access
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Common questions</h2>
        <div className="mt-6 space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-xl border border-[#E5E7E7] bg-white p-5" data-testid={`sales-faq-${f.q.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 32)}`}>
              <summary className="cursor-pointer list-none font-semibold flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-[#004C54] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <div className="mt-3 text-sm text-foreground/65 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t border-black/5 mt-12">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 text-xs text-foreground/55 flex flex-wrap items-center justify-between gap-3">
          <div>© Apex Athletic Mind Consulting</div>
          <a href="/" className="hover:text-foreground/80">Back to main site →</a>
        </div>
      </footer>
    </div>
  );
}
